import PropTypes from 'prop-types';
import { useEffect } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Loader } from '@hivebrite/design-system/Loader';

import { Link } from 'hb-react/frontoffice/components/Link';
import {
  FilePreviewContent,
  FilePreviewNoContent,
} from 'hb-react/shared/components/FilePreview/Common/Content';

const Content = ({ file, isLoading = false, ...rest }) => {
  if (isLoading) {
    return (
      <WrapperStyled>
        <Loader size={50} />
      </WrapperStyled>
    );
  }

  if (!file && !isLoading) {
    return (
      <WrapperStyled>
        <FilePreviewNoContent />
      </WrapperStyled>
    );
  }

  return <NavigationFileWrapper file={file} {...rest} />;
};

Content.propTypes = {
  file: PropTypes.object,
  isLoading: PropTypes.bool,
};

export const NavigationFileWrapper = ({
  categories,
  file,
  hideNextUrl = false,
  hidePreviousUrl = false,
  nextUrl,
  onKeyUp,
  previousUrl,
  nextImage,
  prevImage,
}) => {
  useEffect(() => {
    const handleKeyUp = (event) => {
      if (onKeyUp) {
        onKeyUp(event.keyCode, event);
      }
    };

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [onKeyUp]);

  return (
    <WrapperStyled>
      <PreviousStyled>
        <LinkButton
          to={previousUrl}
          hide={hidePreviousUrl}
          onClick={prevImage}
        />
      </PreviousStyled>
      <FilePreviewContent filePreview={file} categories={categories} />
      <NextStyled>
        <LinkButton to={nextUrl} hide={hideNextUrl} onClick={nextImage} right />
      </NextStyled>
    </WrapperStyled>
  );
};

NavigationFileWrapper.propTypes = {
  categories: PropTypes.array,
  onKeyUp: PropTypes.func,
  previousUrl: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  hideNextUrl: PropTypes.bool,
  hidePreviousUrl: PropTypes.bool,
  nextUrl: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  file: PropTypes.object.isRequired,
};

const getIconWrapper = (right) => () => {
  const iconName = right ? 'chevron-right' : 'chevron-left';

  return (
    <FontAwesomeIcon
      iconName={iconName}
      height="20px"
      iconType={CATEGORIES.SOLID}
    />
  );
};

export const LinkButton = ({ hide = false, to, right = false, onClick }) => {
  if (hide) {
    return null;
  }

  const screenReadersText = right
    ? s__('Accessibility|Go to the next file preview')
    : s__('Accessibility|Go to the previous file preview');

  const IconWrapper = getIconWrapper(right);
  const dataTestId = `arrow-${right ? 'right' : 'left'}`;

  if (onClick) {
    return (
      <Button
        icon={IconWrapper}
        variant="secondary"
        dataTestId={dataTestId}
        screenReadersText={screenReadersText}
        onClick={onClick}
      />
    );
  }

  if (!to) {
    return <DisabledButton icon={IconWrapper} dataTestId={dataTestId} />;
  }

  return (
    <ActiveLink
      icon={IconWrapper}
      to={to}
      dataTestId={dataTestId}
      screenReadersText={screenReadersText}
    />
  );
};

LinkButton.propTypes = {
  hide: PropTypes.bool,
  right: PropTypes.bool,
  to: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export const ActiveLink = ({ icon, to, dataTestId, screenReadersText }) => (
  <Link data-bypass-router to={to} tabIndex={-1}>
    <Button
      variant="secondary"
      dataTestId={dataTestId}
      icon={icon}
      screenReadersText={screenReadersText}
    />
  </Link>
);

export const DisabledButton = ({ icon, dataTestId }) => (
  <Button icon={icon} dataTestId={dataTestId} isDisabled variant="secondary" />
);

DisabledButton.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]).isRequired,
};

export default Content;

const WrapperStyled = styled(Box).attrs({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '100%',
  py: 5,
})`
  border-right: 1px solid ${({ theme }) => theme.colors.horizon};
`;

const PreviousStyled = styled(Box).attrs({
  display: 'block',
  textAlign: 'left',
  ml: { l: 6, xl: 8 },
  mr: { l: 2, xl: 3 },
})``;

const NextStyled = styled(Box).attrs({
  display: 'block',
  textAlign: 'right',
  mr: { l: 6, xl: 8 },
  ml: { l: 2, xl: 3 },
})``;
